import { DateTime } from 'luxon';
import React, { FC } from 'react';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import { CenteredLink } from '@ui/atoms/CenteredLink';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';

import MessageIconCard from '@ui/molecules/MessageIconCard';
import SubmitButton from '@ui/molecules/SubmitButton';

import fullWidth from '@ui/styled/FullWidth';

import { interpolate } from '@utils/butter';

import Attention from '@assets/vectors/attention.inline.svg';

import FolxTheme from '@theme/FolxTheme';

const FullWidthSubmitButton = fullWidth(SubmitButton);
const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);

const SmallUnderlinedLink = styled(CenteredLink)`
	color: black;
	${BasicText} {
		font-size: ${FolxTheme.fontSizes.body.sm};
	}
`;

interface IPastCancelDateAppointmentErrorProps {
	convertedDate: DateTime;
	cta: string;
	description: string;
	header: string;
	onClick: () => void;
	secondaryCta: string;
	subheader: string;
	appointmentType?: string;
	loading?: boolean;
}

const PastCancelDateAppointmentError: FC<IPastCancelDateAppointmentErrorProps> = ({
	appointmentType = 'Clinician',
	convertedDate,
	cta,
	description,
	header,
	loading,
	onClick,
	secondaryCta,
	subheader,
}) => {
	const localizedDate = convertedDate?.toFormat('EEEE, MMMM d');
	const localizedTime = convertedDate?.toFormat('h:mm a ZZZZ');

	return (
		<FullWidthColumn gap="lg">
			<LoadingWrapper
				loading={loading}
				skeleton={
					<>
						<Skeleton structure="single" height="lg" fullWidth />
						<Skeleton structure="single" height="xxxxl" fullWidth />
					</>
				}>
				<FullWidthColumn gap="sm">
					<HeadingText fontSize="sm">{header}</HeadingText>
					<MessageIconCard
						bgColor="cardRed"
						Icon={Attention}
						iconAlt="attention"
						message={description}
					/>
					<FullWidthColumn gap="xssm">
						<BasicText variant="small">{interpolate(subheader, { appointmentType })}</BasicText>
						<BasicText variant="body">{`${localizedDate} at ${localizedTime}`}</BasicText>
					</FullWidthColumn>
				</FullWidthColumn>
				<FullWidthSubmitButton label={cta} onClick={onClick} variant="secondary" />
				<SmallUnderlinedLink
					variant="underline"
					to="/app/member/reactivate_membership/summary/?backTo=billing">
					{secondaryCta}
				</SmallUnderlinedLink>
			</LoadingWrapper>
		</FullWidthColumn>
	);
};

export default PastCancelDateAppointmentError;
